import type { TimeframeStore } from "../data/DataStore";
import { DSZoneEngine, zoneTag, type ZoneType } from "../chart/indicators/DSzone";
import { nyDate } from "../chart/util";
import type { Levels, Side, ZoneInfo } from "./types";

// Only zones formed inside the New York morning session window.
export const WINDOW_START_MIN = 9 * 60 + 30; // 09:30 ET
export const WINDOW_END_MIN = 11 * 60 + 30; // 11:30 ET
export const WINDOW_LABEL = "09:30–11:30 ET";

const nyMinutes = (t: number) => {
  const d = nyDate(t);
  return d.getUTCHours() * 60 + d.getUTCMinutes() + d.getUTCSeconds() / 60;
};
const nyDay = (t: number) => {
  const d = nyDate(t);
  return d.getUTCFullYear() * 10000 + d.getUTCMonth() * 100 + d.getUTCDate();
};

// Cheap pre-check on the signal candle alone (closes inside the window).
export function signalInWindow(signalOpen: number, tfSec: number, startMin = WINDOW_START_MIN, endMin = WINDOW_END_MIN): boolean {
  if (!isFinite(signalOpen)) return false;
  const close = signalOpen + tfSec;
  const m = nyMinutes(close);
  return m > startMin && m <= endMin && nyDay(signalOpen) === nyDay(close - 1);
}

// Whole zone inside the window: leg-in opens at/after 09:30, signal closes by 11:30, same day.
export function zoneInWindow(store: TimeframeStore, k: number, t: ZoneType, tfSec: number, startMin = WINDOW_START_MIN, endMin = WINDOW_END_MIN): boolean {
  const legInOpen = store.timeAt(k - t.n - t.b);
  const signalOpen = store.timeAt(k);
  if (!isFinite(legInOpen) || !signalInWindow(signalOpen, tfSec, startMin, endMin)) return false;
  return nyMinutes(legInOpen) >= startMin && nyDay(legInOpen) === nyDay(signalOpen);
}

export const TICK = 0.25; // MNQ tick size
export const RR_DEFAULT = 3;
export const ENGINE_VERSION = "DSzone v2.13";

export const roundTick = (p: number) => Math.round(p / TICK) * TICK;

// How far forward we look to judge the outcome: ~8h of trading, bounded in bars.
export function horizonBars(tfSec: number): number {
  return Math.max(200, Math.min(30000, Math.ceil((8 * 3600) / tfSec)));
}

export interface FoundZone {
  k: number;
  t: ZoneType;
}

// Pick a random point in history and scan forward (wrapping) with the DSzone
// engine until we hit a zone that hasn't been reviewed yet (and passes `accept`).
export async function findRandomZone(
  store: TimeframeStore,
  tf: string,
  tfSec: number,
  exclude: Set<string>,
  onProgress?: (scanned: number) => void,
  accept?: (k: number, t: ZoneType) => Promise<boolean> // extra filter (e.g. "must hit target")
): Promise<FoundZone | null> {
  const N = store.barCount;
  const lo = 300; // ATB warm-up (100) + the 100-candle lead-in + pattern
  let hi = N - 1 - horizonBars(tfSec); // keep room to judge the outcome
  if (hi <= lo + 1000) hi = N - 1;
  if (hi <= lo) return null;

  const eng = new DSZoneEngine(store, tfSec);
  const WINDOW = 4000;
  const total = Math.min(250_000, hi - lo);
  let pos = lo + Math.floor(Math.random() * (hi - lo));
  let scanned = 0;
  while (scanned < total) {
    const end = Math.min(hi, pos + WINDOW);
    await store.ensureRange(pos - 150, end + 1, 0);
    for (let k = pos; k < end; k++) {
      if (!signalInWindow(store.timeAt(k), tfSec)) continue; // skip before the (costly) indicator
      const res = eng.at(k);
      if (!res) continue;
      const t = res.types[0];
      if (!zoneInWindow(store, k, t, tfSec)) continue;
      const key = `${tf}:${store.timeAt(k)}`;
      if (!exclude.has(key) && (!accept || (await accept(k, t)))) return { k, t };
    }
    scanned += end - pos;
    pos = end >= hi ? lo : end;
    onProgress?.(scanned);
    await new Promise((r) => setTimeout(r, 0)); // keep the UI responsive
  }
  return null;
}

// Zone = the boring (base) candles' wicks only: highest high and lowest low.
// Demand: proximal = boring high-high, distal = boring low-low. Supply mirrors it.
export function zoneGeometry(store: TimeframeStore, k: number, t: ZoneType, tf?: string): ZoneInfo {
  const n = t.n;
  const b = t.b;
  const legIn = k - n - b;
  const baseStart = legIn + 1;
  const baseEnd = k - n;
  const demand = t.name === "DBR" || t.name === "RBR";
  let highHigh = -Infinity;
  let lowLow = Infinity;
  for (let i = baseStart; i <= baseEnd; i++) {
    highHigh = Math.max(highHigh, store.highAt(i));
    lowLow = Math.min(lowLow, store.lowAt(i));
  }
  const proximal = demand ? highHigh : lowLow;
  const distal = demand ? lowLow : highHigh;

  const side: Side = demand ? "long" : "short";
  const signalTime = store.timeAt(k);
  return {
    zoneId: tf ? `${t.name}-${tf}-${Math.round(signalTime).toString(36).toUpperCase()}` : undefined,
    type: t.name,
    n,
    b,
    w: t.w,
    e: t.e,
    tag: zoneTag(t),
    side,
    proximal,
    distal,
    legInTime: store.timeAt(legIn),
    baseStartTime: store.timeAt(baseStart),
    signalTime,
    signalIndex: k,
    atr: store.atrAt(k),
  };
}

// Entry on the proximal wick, stop on the distal wick (boring candles), target at 3R.
export function autoLevels(z: ZoneInfo): Levels {
  const atr = isFinite(z.atr) && z.atr > 0 ? z.atr : 4;
  const long = z.side === "long";
  const entry = roundTick(z.proximal);
  let stop = roundTick(z.distal);
  let risk = Math.abs(entry - stop);
  if (risk < TICK) {
    risk = Math.max(1, roundTick(atr * 0.5));
    stop = long ? entry - risk : entry + risk;
  }
  const target = roundTick(long ? entry + RR_DEFAULT * risk : entry - RR_DEFAULT * risk);
  return { entry, stop, target };
}

export function rrOf(lv: Levels): number {
  const risk = Math.abs(lv.entry - lv.stop);
  return risk > 0 ? Math.abs(lv.target - lv.entry) / risk : 0;
}

// Pattern + a little context, for the training dataset.
export function snapshotBars(store: TimeframeStore, z: ZoneInfo, contextBars = 10): number[][] {
  const from = Math.max(0, z.signalIndex - z.n - z.b - contextBars);
  const out: number[][] = [];
  for (let i = from; i <= z.signalIndex; i++) {
    out.push([store.timeAt(i), store.openAt(i), store.highAt(i), store.lowAt(i), store.closeAt(i), store.volumeAt(i), store.atrAt(i)]);
  }
  return out;
}
